// [목적] 최상위 레이아웃 — 데스크톱은 Sidebar+Outlet, 모바일은 Outlet+BottomTabBar
// [이유] docs/플리오랩/web 은 사이드바 셸, docs/플리오랩/screens 는 하단 탭 셸로 별도 설계.
//         W3(design-alignment): collapsed boolean + toggleSidebar 추가 — 데스크톱 사이드바 collapse 토글 배선.
// [사이드 이팩트] Outlet context 에 collapsed/toggleSidebar 추가. 기존 소비처(viewport/openSidebar) 는 회귀 없음.
//                 사이드바 너비가 collapsed 여부에 따라 232→60px 전환. main 영역 flex:1 유지로 자동 확장.
// [수정 시 고려사항] collapsed 는 세션 상태(localStorage 지속 없음) — 과설계 방지.
//                   모바일은 collapsed 와 무관하게 사이드바 미노출 + BottomTabBar 렌더.

import { Box } from '@mui/material';
import { useState } from 'react';
import { Outlet } from 'react-router-dom';

import { BottomTabBar } from './components/layout/BottomTabBar.js';
import { Sidebar } from './components/layout/Sidebar.js';
import { useViewport } from './hooks/useViewport.js';

/** 데스크톱 사이드바 최대 폭 (펼침) */
const SIDEBAR_EXPANDED_WIDTH = 232;
/** 데스크톱 사이드바 최소 폭 (접힘, 아이콘만) */
const SIDEBAR_COLLAPSED_WIDTH = 60;

export function App(): JSX.Element {
  const viewport = useViewport();
  const [mobileOpen, setMobileOpen] = useState(false);
  /**
   * [목적] 데스크톱 사이드바 collapse 상태
   * [이유] W3-1(design-alignment): 시안 shell.jsx 의 collapsed 토글 연결.
   *         세션 상태로만 유지(localStorage 지속 없음 — 과설계 방지).
   * [사이드 이팩트] Outlet context → 페이지에서 useShell() 로 접근 가능.
   * [수정 시 고려사항] 모바일에서는 collapsed=false 고정 반환.
   */
  const [collapsed, setCollapsed] = useState(false);

  const sidebarWidth = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_EXPANDED_WIDTH;

  const toggleSidebar = (): void => setCollapsed((prev) => !prev);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex' }}>
      {!viewport.isMobile && (
        <Sidebar
          width={sidebarWidth}
          collapsed={collapsed}
          onToggle={toggleSidebar}
          mobile={false}
          mobileOpen={mobileOpen}
          onClose={() => setMobileOpen(false)}
        />
      )}
      <Box
        component="main"
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          pb: viewport.isMobile ? 'calc(70px + env(safe-area-inset-bottom))' : 0,
        }}
      >
        <Outlet
          context={{
            viewport,
            openSidebar: () => setMobileOpen(true),
            collapsed: viewport.isMobile ? false : collapsed,
            toggleSidebar,
          }}
        />
      </Box>
      {viewport.isMobile && <BottomTabBar />}
    </Box>
  );
}
